/**
 * Review mode — the read-only "see everything at once" view of one stored
 * version (what the old Priced Scenarios drilldown was).
 *
 *   ┌ KPI strip (one card): navy headline cell + up to five KPI cells ┐
 *   ├ panels — the sizer's own result cards, three across            ┤
 *   └ Inputs · v3 — a 4-column grid of the inputs that priced it     ┘
 *
 * The sizer supplies the strip and the panels (each sizer's answer has a
 * different shape); the inputs grid and the frame are common. Everything shown
 * is the STORED response for the version on screen — never a re-price.
 */
import Box from "@mui/material/Box";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { SZ, EmptyState } from "../sizerUi";
import { NUM, WsSecondaryButton } from "./workspaceParts";

/* ============================================================ KPI strip */

/**
 * One card, cells divided by hairlines. The first cell (the headline) is 1.35×
 * the others. Wraps to three across on a narrower window and two on a phone.
 */
export const KpiStrip = ({ hero, children }) => (
  <Box
    sx={{
      bgcolor: SZ.card,
      border: `1px solid ${SZ.line}`,
      borderRadius: SZ.radius,
      boxShadow: SZ.shadow,
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        // Cells draw their own right/bottom hairlines; the -1px pulls the last
        // column and row under the card edge so it is never doubled.
        mr: "-1px",
        mb: "-1px",
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, minmax(0,1fr))",
          md: "repeat(3, minmax(0,1fr))",
          xl: "1.35fr repeat(5, minmax(0,1fr))",
        },
        "& > *": {
          borderRight: `1px solid ${SZ.line}`,
          borderBottom: `1px solid ${SZ.line}`,
        },
      }}
    >
      {hero && (
        <Box
          sx={{
            gridRow: { md: "span 2", xl: "auto" },
            gridColumn: { xs: "span 2", md: "auto" },
          }}
        >
          {hero}
        </Box>
      )}
      {children}
    </Box>
  </Box>
);

const kpiLabelSx = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: ".09em",
  textTransform: "uppercase",
};

/** The navy headline cell — "Required coupon · v3", the figure, its build-up chips. */
export const KpiHero = ({ label, value, sub, chips = [] }) => (
  <Box
    sx={{
      height: "100%",
      bgcolor: SZ.heroBg,
      color: "#fff",
      p: "18px 18px 16px",
    }}
  >
    <Txt component="div" sx={{ ...kpiLabelSx, color: SZ.heroMuted }}>
      {label}
    </Txt>
    <Txt
      component="div"
      sx={{
        fontFamily: SZ.mono,
        fontSize: 30,
        fontWeight: 600,
        letterSpacing: "-.02em",
        m: "6px 0 2px",
        ...NUM,
      }}
    >
      {value}
    </Txt>
    {sub && (
      <Txt component="div" sx={{ fontSize: 12, color: SZ.heroMuted }}>
        {sub}
      </Txt>
    )}
    {chips.length > 0 && (
      <Flx g={0.75} sx={{ mt: 1.25, flexWrap: "wrap" }}>
        {chips.map((c) => (
          <Txt
            key={c.k}
            sx={{
              fontFamily: SZ.mono,
              fontSize: 10.5,
              color: SZ.heroChipInk,
              bgcolor: SZ.heroChip,
              borderRadius: "5px",
              p: "3px 7px",
              whiteSpace: "nowrap",
            }}
          >
            {c.k}{" "}
            <Box component="span" sx={{ color: "#fff" }}>
              {c.v}
            </Box>
          </Txt>
        ))}
      </Flx>
    )}
  </Box>
);

/** A white KPI cell. `tone`: "good" | "bad" | "warn" colors the value. */
export const KpiCell = ({ label, value, caption, tone }) => (
  <Box sx={{ p: "18px 18px 16px", minWidth: 0 }}>
    <Txt component="div" sx={{ ...kpiLabelSx, color: SZ.ink500 }}>
      {label}
    </Txt>
    <Txt
      component="div"
      sx={{
        fontFamily: SZ.mono,
        fontSize: 19,
        fontWeight: 600,
        mt: "8px",
        color:
          tone === "good"
            ? SZ.good
            : tone === "bad"
              ? SZ.bad
              : tone === "warn"
                ? SZ.warn
                : SZ.ink900,
        // Wraps rather than truncating: a word answer ("Sufficient Liquidity") can
        // outrun a narrow KPI cell, and a clipped verdict reads as a bug.
        lineHeight: 1.25,
        overflowWrap: "break-word",
        ...NUM,
      }}
    >
      {value ?? "—"}
    </Txt>
    {caption && (
      <Txt component="div" sx={{ fontSize: 12, color: SZ.ink500, mt: "4px" }}>
        {caption}
      </Txt>
    )}
  </Box>
);

/* ============================================================ Inputs grid */

/** `scenarioInputSummary` cells as a 4-column key/value grid (not a data table). */
export const InputsGrid = ({ cells }) =>
  cells?.length ? (
    <Box
      sx={{
        bgcolor: SZ.card,
        border: `1px solid ${SZ.line}`,
        borderRadius: SZ.radius,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          mr: "-1px",
          mb: "-1px",
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0,1fr))",
            md: "repeat(4, minmax(0,1fr))",
          },
          "& > *": {
            borderRight: `1px solid ${SZ.line}`,
            borderBottom: `1px solid ${SZ.line}`,
          },
        }}
      >
        {cells.map((f) => (
          <Box key={f.k} sx={{ p: "12px 14px", minWidth: 0 }}>
            <Txt
              component="div"
              sx={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: ".06em",
                textTransform: "uppercase",
                color: SZ.ink400,
              }}
            >
              {f.k}
            </Txt>
            <Txt
              component="div"
              sx={{
                fontFamily: SZ.mono,
                fontSize: 13,
                color: SZ.ink900,
                mt: "4px",
                wordBreak: "break-word",
              }}
            >
              {f.v}
              {f.note && (
                <Box
                  component="span"
                  sx={{
                    fontFamily: "inherit",
                    fontSize: 11.5,
                    color: SZ.ink400,
                    ml: 0.75,
                  }}
                >
                  {f.note}
                </Box>
              )}
            </Txt>
          </Box>
        ))}
      </Box>
    </Box>
  ) : null;

/* ============================================================ Layout */

export default function ScenarioReview({
  hasResult,
  kpis,
  panels,
  shownVersion,
  pricedAt,
  pricedBy,
  inputCells,
  onEdit,
}) {
  return (
    <Flx column g={3}>
      {hasResult ? (
        <>
          {kpis}
          {panels}
        </>
      ) : (
        <Box
          sx={{
            bgcolor: SZ.card,
            border: `1px solid ${SZ.line}`,
            borderRadius: SZ.radius,
            p: 4,
          }}
        >
          <EmptyState
            icon={<ReceiptLongOutlined />}
            title="Not priced yet"
            description="Open the inputs and price this scenario to see its results here."
          />
          <Flx center sx={{ mt: 1 }}>
            <WsSecondaryButton onClick={onEdit} startIcon={<EditOutlined />}>
              Edit inputs
            </WsSecondaryButton>
          </Flx>
        </Box>
      )}

      <Box>
        <Flx jb ac g={1.5} sx={{ mb: 1.25, flexWrap: "wrap" }}>
          <Box>
            <Txt
              component="div"
              sx={{ fontSize: 15, fontWeight: 700, color: SZ.ink900 }}
            >
              Inputs{shownVersion ? ` · v${shownVersion}` : ""}
            </Txt>
            <Txt
              component="div"
              sx={{ fontSize: 12.5, color: SZ.ink500, mt: "2px" }}
            >
              {pricedAt
                ? `Priced ${formatDateTime(pricedAt, "MMM d, h:mm a")}${pricedBy ? ` by ${pricedBy}` : ""}. The stored response — not a re-price.`
                : "The inputs stored on this scenario."}
            </Txt>
          </Box>
          <WsSecondaryButton onClick={onEdit} startIcon={<EditOutlined />}>
            Edit inputs
          </WsSecondaryButton>
        </Flx>
        <InputsGrid cells={inputCells} />
      </Box>
    </Flx>
  );
}
